/**
 * Content Security settings (CS-4) — per-workspace share defaults + access control.
 *
 * Built 2026-08-24 (replaces the "Soon" placeholder). Owner-only (sidebar item
 * is access:"owner"; the server also gates PUT via ACTIONS.CHANGE_WORKSPACE_SETTINGS).
 * Backed by workspace_settings (migration 0020) via GET/PUT
 * /api/editor/workspace-settings. Consumed at share-create (defaults +
 * require-passphrase) and guest identify (allowed email domains).
 */

import { useEffect, useMemo, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import {
  useWorkspaceSettings,
  useUpdateWorkspaceSettings,
  type WorkspaceSettingsUpdate,
} from "@/hooks/use-workspace-settings";
import { authErrorMessage } from "@/lib/auth-errors";
import { FeatureLock } from "@/components/billing/feature-lock";
import { useHasFeature } from "@/hooks/use-plan";

const EXPIRY_OPTIONS: Array<{ label: string; days: number }> = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "10 years", days: 3650 },
];

/** Split a free-text list into clean, de-duplicated domains (lowercase, no '@'). */
function parseDomains(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[\s,]+/)) {
    const d = raw.trim().toLowerCase().replace(/^@/, "");
    if (!d || seen.has(d)) continue;
    seen.add(d);
    out.push(d);
  }
  return out;
}

export function ContentSecuritySettingsPage() {
  const { data, isLoading, isError } = useWorkspaceSettings();
  const update = useUpdateWorkspaceSettings();

  const [requirePassphrase, setRequirePassphrase] = useState(false);
  const [defaultExpiryDays, setDefaultExpiryDays] = useState(30);
  const [defaultDownloadAllowed, setDefaultDownloadAllowed] = useState(false);
  const [domainsText, setDomainsText] = useState("");
  const [saved, setSaved] = useState(false);

  // Hydrate the draft whenever server data changes (initial load + post-save).
  useEffect(() => {
    if (!data) return;
    setRequirePassphrase(data.requirePassphrase);
    setDefaultExpiryDays(data.defaultExpiryDays);
    setDefaultDownloadAllowed(data.defaultDownloadAllowed);
    setDomainsText(data.allowedEmailDomains.join("\n"));
  }, [data]);

  const canEdit = data?.canEdit ?? false;
  // Plan gates — share defaults need paid tiers; authorized domains need Agency.
  const canShareControls = useHasFeature("shareControls");
  const canExpiry = useHasFeature("shareExpiryControl");
  const canAuthDomains = useHasFeature("authorizedDomains");

  const dirty = useMemo(() => {
    if (!data) return false;
    return (
      requirePassphrase !== data.requirePassphrase ||
      defaultExpiryDays !== data.defaultExpiryDays ||
      defaultDownloadAllowed !== data.defaultDownloadAllowed ||
      JSON.stringify(parseDomains(domainsText)) !== JSON.stringify(data.allowedEmailDomains)
    );
  }, [data, requirePassphrase, defaultExpiryDays, defaultDownloadAllowed, domainsText]);

  async function handleSave() {
    setSaved(false);
    try {
      // Omit plan-gated fields the workspace can't set — the server 403s otherwise.
      const body: WorkspaceSettingsUpdate = {};
      if (canShareControls) {
        body.requirePassphrase = requirePassphrase;
        body.defaultDownloadAllowed = defaultDownloadAllowed;
      }
      if (canExpiry) body.defaultExpiryDays = defaultExpiryDays;
      if (canAuthDomains) body.allowedEmailDomains = parseDomains(domainsText);
      await update.mutateAsync(body);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      /* error surfaced below via update.isError */
    }
  }

  if (isLoading) {
    return (
      <div>
        <SettingsPageHeader
          title="Content Security"
          subtitle="Set default protections for every new share link in your workspace."
        />
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[62px] animate-pulse rounded-xl border border-border bg-secondary/30" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <SettingsPageHeader
          title="Content Security"
          subtitle="Set default protections for every new share link in your workspace."
        />
        <div className="rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
          Couldn't load your content-security settings. Refresh to try again.
        </div>
      </div>
    );
  }

  return (
    <div>
      <SettingsPageHeader
        title="Content Security"
        subtitle="Set default protections for every new share link in your workspace."
      />

      {!canEdit && (
        <div className="mb-6 rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
          Only the workspace owner can change these settings.
        </div>
      )}

      {/* ── Share defaults ─────────────────────────────────────────────── */}
      <SectionLabel title="Share defaults" />
      <p className="mb-4 text-sm text-muted-foreground">
        Pre-filled when someone creates a new share. They can still change them per link.
      </p>

      <div className="flex flex-col gap-3">
        <FeatureLock feature="shareExpiryControl">
          <div className="rounded-xl border border-border bg-secondary/30 px-4 py-3">
            <div className="text-sm font-medium text-foreground">Default link expiry</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {EXPIRY_OPTIONS.map((opt) => (
                <button
                  key={opt.days}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setDefaultExpiryDays(opt.days)}
                  className={`rounded-md border px-2.5 py-1 text-xs transition-colors disabled:opacity-50 ${
                    defaultExpiryDays === opt.days
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </FeatureLock>

        <FeatureLock feature="shareControls">
          <ToggleRow
            label="Allow downloads by default"
            description="New shares let guests download the original file unless you turn this off per link."
            checked={canShareControls ? defaultDownloadAllowed : true}
            disabled={!canEdit}
            onCheckedChange={setDefaultDownloadAllowed}
          />
        </FeatureLock>
      </div>

      {/* ── Access control ─────────────────────────────────────────────── */}
      <div className="mt-10">
        <SectionLabel title="Access control" />

        <div className="flex flex-col gap-3">
          <FeatureLock feature="shareControls">
            <ToggleRow
              label="Require a passphrase on every share"
              description="Block creating a share link without a passphrase. Existing links are unaffected."
              checked={canShareControls ? requirePassphrase : false}
              disabled={!canEdit}
              onCheckedChange={setRequirePassphrase}
            />
          </FeatureLock>

          <FeatureLock feature="authorizedDomains">
            <div className="rounded-xl border border-border bg-secondary/30 px-4 py-3">
              <div className="text-sm font-medium text-foreground">Allowed email domains</div>
              <div className="mt-0.5 text-sm text-muted-foreground">
                Guests must enter an email at one of these domains to open a share. Leave empty to allow anyone.
                One domain per line (e.g. acme.com).
              </div>
              <textarea
                value={domainsText}
                disabled={!canEdit}
                onChange={(e) => setDomainsText(e.target.value)}
                rows={4}
                spellCheck={false}
                placeholder={"acme.com\nstudio.co"}
                className="mt-3 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              />
            </div>
          </FeatureLock>
        </div>
      </div>

      {/* ── Save bar ───────────────────────────────────────────────────── */}
      {canEdit && (
        <div className="mt-8 flex items-center gap-3">
          <Button onClick={handleSave} disabled={!dirty || update.isPending}>
            {update.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
              </>
            ) : (
              "Save changes"
            )}
          </Button>
          {saved && !dirty && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-primary" /> Saved
            </span>
          )}
          {update.isError && (
            <span className="text-sm text-destructive">{authErrorMessage(update.error)}</span>
          )}
        </div>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-secondary/30 px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-sm text-muted-foreground">{description}</div>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} aria-label={label} />
    </div>
  );
}

function SectionLabel({ title }: { title: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    </div>
  );
}
